import React, { useState, useMemo, useEffect } from 'react';

import {
    EdiObject,
    Edi835Payment,
    Edi835ProviderAdjustment,
    Edi837Claim,
    Edi834MemberCoverage,
    Edi277ClaimStatus,
    Edi277ReceiverStatus,
    Edi277ProviderStatus,
} from './types';
import { formatDisplayDate } from '../../helpers/dataHelper';
import { formatCurrency } from './viewerHelpers';

import Field from './components/Field';
import StatusBadge from './components/StatusBadge';
import TabBarWithArrows from './components/TabBarWithArrows';
import StatusInfoDisplay from './components/StatusInfoDisplay';
import Payment835Viewer from './components/Payment835Viewer';
import Claim837Viewer from './components/Claim837Viewer';
import MemberCoverage834Viewer from './components/MemberCoverage834Viewer';
import ClaimStatus277Viewer from './components/ClaimStatus277Viewer';
import Raw276or277Viewer from './components/Raw276or277Viewer';
import Raw279Viewer from './components/Raw279Viewer';
import Raw810Viewer from './components/Raw810Viewer';

interface EdiClaimViewerProps {
    content: string;
}

// Helper to detect if data is raw 276/277 format
const isRaw276or277Format = (data: unknown[]): { is276or277: boolean; transactionType?: '276' | '277' } => {
    // Check for ST segment with 276 or 277 transaction set identifier
    const stSegment = data.find((seg: unknown) =>
        (seg as { segment_id?: string }).segment_id === 'ST'
    ) as { transaction_set_identifier_code?: { code?: string } } | undefined;

    const code = stSegment?.transaction_set_identifier_code?.code;
    if (code === '277') return { is276or277: true, transactionType: '277' };
    if (code === '276') return { is276or277: true, transactionType: '276' };
    return { is276or277: false };
};

// Helper to detect if data is raw 279 (270/271) format
const isRaw279Format = (data: unknown[]): boolean => {
    // Check for ST segment with 270 or 271 transaction set identifier (279 uses these)
    return data.some((seg: unknown) => {
        const segId = (seg as { segment_id?: string }).segment_id;
        const code = (seg as { transaction_set_identifier_code?: { code?: string } }).transaction_set_identifier_code?.code;
        return segId === 'ST' && (code === '270' || code === '271');
    });
};

// Helper to detect if data is raw 810 format
const isRaw810Format = (data: unknown[]): boolean => {
    // Check for ST segment with 810 transaction set identifier
    return data.some((seg: unknown) =>
        (seg as { segment_id?: string }).segment_id === 'ST' &&
        ((seg as { transaction_set_identifier_code?: { code?: string } }).transaction_set_identifier_code?.code === '810')
    );
};

// ===== Main Component =====

const EdiClaimViewer: React.FC<EdiClaimViewerProps> = ({ content }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const data = useMemo(() => {
        try {
            const parsed = JSON.parse(content);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return null;
        }
    }, [content]);

    // Reset selectedIndex when content changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [content]);

    // Also reset if data changes (in case content string is same but data is different)
    useEffect(() => {
        if (data && data.length > 0) {
            setSelectedIndex(0);
        }
    }, [data]);

    if (!data) {
        return (
            <div className="p-4 text-sm text-gray-500">
                Unable to parse EDI data. Please check the format.
            </div>
        );
    }

    // Check if this is raw 276/277 segment format
    const raw276or277 = isRaw276or277Format(data);
    if (raw276or277.is276or277 && raw276or277.transactionType) {
        return <Raw276or277Viewer data={data} transactionType={raw276or277.transactionType} />;
    }

    // Check if this is raw 279 (270/271) segment format
    if (isRaw279Format(data)) {
        return <Raw279Viewer data={data} />;
    }

    // Check if this is raw 810 segment format
    if (isRaw810Format(data)) {
        return <Raw810Viewer data={data} />;
    }

    // Filter out ERROR and WARNING objects
    const validObjects = data.filter((item: EdiObject) =>
        item.objectType && !['ERROR', 'WARNING'].includes(item.objectType)
    );

    if (validObjects.length === 0) {
        return (
            <div className="p-4 text-sm text-gray-500">
                No valid EDI objects found in this file.
            </div>
        );
    }

    // Group by object type
    const payments = validObjects.filter((item: EdiObject) => item.objectType === 'PAYMENT') as Edi835Payment[];
    const providerAdjustments = validObjects.filter((item: EdiObject) => item.objectType === 'PROVIDER_ADJUSTMENT') as Edi835ProviderAdjustment[];
    const claims = validObjects.filter((item: EdiObject) => item.objectType === 'CLAIM') as Edi837Claim[];
    const memberCoverages = validObjects.filter((item: EdiObject) => item.objectType === 'MEMBER_COVERAGE') as Edi834MemberCoverage[];

    // Ensure selectedIndex is valid and use first item if only one exists
    const safeIndex = validObjects.length === 1 ? 0 : Math.min(selectedIndex, validObjects.length - 1);
    const currentItem = validObjects[safeIndex];

    const renderViewer = () => {
        switch (currentItem.objectType) {
            case 'PAYMENT':
                return <Payment835Viewer payment={currentItem as Edi835Payment} providerAdjustments={providerAdjustments} />;
            case 'CLAIM':
                return <Claim837Viewer claim={currentItem as Edi837Claim} />;
            case 'MEMBER_COVERAGE':
                return <MemberCoverage834Viewer coverage={currentItem as Edi834MemberCoverage} />;
            case 'CLAIM_STATUS':
                return <ClaimStatus277Viewer status={currentItem as Edi277ClaimStatus} />;
            case 'RECEIVER_STATUS':
            case 'PROVIDER_STATUS':
                // Show batch-level status
                const batchStatus = currentItem as Edi277ReceiverStatus | Edi277ProviderStatus;
                return (
                    <div className="p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <StatusBadge status={currentItem.objectType} type="type" />
                        </div>
                        {batchStatus.batchStatus && (
                            <div className="bg-white rounded-lg border p-4">
                                <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                                    <Field label="Accepted Qty" value={batchStatus.batchStatus.acceptedQuantity} />
                                    <Field label="Accepted Amt" value={formatCurrency(batchStatus.batchStatus.acceptedAmount)} />
                                    <Field label="Rejected Qty" value={batchStatus.batchStatus.rejectedQuantity} />
                                    <Field label="Rejected Amt" value={formatCurrency(batchStatus.batchStatus.rejectedAmount)} />
                                </div>
                                {batchStatus.batchStatus.statusInfos?.map((info, idx) => (
                                    <StatusInfoDisplay key={idx} statusInfo={info} />
                                ))}
                            </div>
                        )}
                    </div>
                );
            case 'PROVIDER_ADJUSTMENT':
                // Provider adjustments are shown with payments
                return (
                    <div className="p-4">
                        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                            <Field label="Provider ID" value={(currentItem as Edi835ProviderAdjustment).providerIdentifier} />
                            <Field label="Fiscal Period" value={formatDisplayDate((currentItem as Edi835ProviderAdjustment).fiscalPeriodDate)} />
                        </div>
                        <div className="mt-4">
                            {(currentItem as Edi835ProviderAdjustment).adjustments?.map((adj, i) => (
                                <div key={i} className="flex justify-between py-2 border-b">
                                    <span className="font-mono">{adj.reason?.code}</span>
                                    <span className="text-gray-600">{adj.reason?.desc}</span>
                                    <span className="text-red-600">{formatCurrency(adj.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            default:
                return <div className="p-4 text-gray-500">Unknown EDI object type: {currentItem.objectType}</div>;
        }
    };

    const claimStatuses = validObjects.filter((item: EdiObject) =>
        ['CLAIM_STATUS', 'RECEIVER_STATUS', 'PROVIDER_STATUS'].includes(item.objectType)
    );

    return (
        <div className="flex flex-col h-full w-full">
            {renderViewer()}

            {/* Object selector for multi-object files */}
            {validObjects.length > 1 && (
                <div className="bg-white border-t border-gray-200 px-2 py-2">
                    <TabBarWithArrows variant="footer">
                        <span className="text-sm text-gray-500 shrink-0">Records:</span>
                        {payments.length > 0 && <span className="text-sm text-gray-400 shrink-0">{payments.length} payments</span>}
                        {claims.length > 0 && <span className="text-sm text-gray-400 shrink-0">{claims.length} claims</span>}
                        {memberCoverages.length > 0 && <span className="text-sm text-gray-400 shrink-0">{memberCoverages.length} members</span>}
                        {claimStatuses.length > 0 && <span className="text-sm text-gray-400 shrink-0">{claimStatuses.length} statuses</span>}
                        <span className="text-sm text-gray-400 shrink-0">|</span>
                        {validObjects.map((item: EdiObject, idx: number) => (
                            <button
                                key={idx}
                                onClick={() => setSelectedIndex(idx)}
                                className={`px-2 py-1 text-sm rounded shrink-0 ${
                                    idx === selectedIndex
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                title={item.objectType}
                            >
                                {idx + 1}
                            </button>
                        ))}
                    </TabBarWithArrows>
                </div>
            )}
        </div>
    );
};

export default EdiClaimViewer;
